import { zodResolver } from '@hookform/resolvers/zod';
import { Globe, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { z } from 'zod';
import useLocale from '../contexts/LocaleContext';
import { isAuthenticated, useLogin } from '../hooks/useAuth';
import logoRw from '../logo-rw.png';
import { Input } from '../components/ui/Input';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const loginMutation = useLogin();
  const { locale, setLocale, dictionary } = useLocale();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  if (isAuthenticated()) {
    return <Navigate to='/facilities' replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    await loginMutation.mutateAsync({
      username: data.username!,
      password: data.password!,
    });
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4'>
      <div className='max-w-sm w-full relative'>
        <div className='bg-white rounded-lg  p-8 relative'>
          {/* Language Switcher */}
          <div className='absolute top-4 right-4'>
            <button
              onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
              className='p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors'
              title='Change language'
            >
              <Globe className='h-5 w-5' />
            </button>

            {/* Language Dropdown */}
            {languageMenuOpen && (
              <>
                <div className='fixed inset-0 z-10' onClick={() => setLanguageMenuOpen(false)} />
                <div className='absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-20'>
                  <div className='py-1'>
                    <button
                      onClick={() => {
                        setLocale('en');
                        setLanguageMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                        locale === 'en' ? 'bg-secondary text-primary' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🇺🇸 English
                    </button>
                    <button
                      onClick={() => {
                        setLocale('rw');
                        setLanguageMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                        locale === 'rw' ? 'bg-secondary text-primary' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🇷🇼 Kinyarwanda
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='text-center mb-8'>
            <div className='flex justify-center mb-4'>
              <img src={logoRw} alt='Republic of Rwanda Logo' className='h-16 w-16' />
            </div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>{dictionary['app.title']}</h1>
            <p className='text-gray-600'>{dictionary['auth.welcome']}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <div>
              <Input
                id='username'
                type='text'
                placeholder={dictionary['auth.email']}
                autoComplete='username'
                error={errors.username?.message}
                {...register('username')}
              />
            </div>

            <div>
              <div className='flex items-center justify-between mb-2'>
                <label htmlFor='password' className='text-sm font-medium text-gray-700'>
                  {dictionary['auth.password']}
                </label>
                <Link
                  to='/forgot-password'
                  className='text-sm text-primary hover:text-primary/80 font-medium focus:outline-none'
                >
                  {dictionary['auth.forgot_password']}
                </Link>
              </div>
              <Input
                id='password'
                type='password'
                placeholder={dictionary['auth.password']}
                autoComplete='current-password'
                error={errors.password?.message}
                {...register('password')}
              />
            </div>

            <button
              type='submit'
              disabled={isSubmitting || loginMutation.isPending}
              className='w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSubmitting || loginMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin inline mr-2' />
                  {dictionary['common.loading']}
                </>
              ) : (
                dictionary['auth.login_button']
              )}
            </button>
          </form>

          <div className='mt-4 text-center'>
            <p className='text-sm text-gray-600'>
              {dictionary['login.no_account'] || "Don't have an account?"}{' '}
              <Link to='/register' className='text-primary hover:text-primary/80 font-medium'>
                {dictionary['login.create_account'] || 'Create Account'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
